import * as React from 'react';
import * as c from 'models/constants';
import { translate } from 'react-i18next';
import { connect } from 'react-redux';
import { Button, Modal, WhiteButton } from 'components';
import { Dispatch } from 'redux';
import { IStore } from 'redux/IStore';
import { reduxForm } from 'redux-form';
import { InjectedFormProps, ITranslateProps } from 'models/common';
import { ButtonsSection, PortalPageSection } from 'components/PortalStructure';
import { closeModal } from 'redux/modules/modal';
import { IBookingTimeTableForm } from 'models/shop/bookingtimetables';
import { WorkDayPeriods } from '../WorkDayPeriods';
import {
	IEditWeekWorkTimeOwnProps,
	dayMinMaxTime,
	officeMinMaxTime,
	stateToEditWeeklyWorkTimeProps,
	submitForm,
} from '../helpers';
import renderIcon from 'helpers/renderIcon';
import { Field } from 'redux-form';
import { BookingDatePicker } from 'components/FormControls';
import moment = require('moment');
import { required } from 'helpers/validationattributes';
import { formatDuration } from 'helpers/common';
import { momentWithTimezone } from 'redux/modules/shop/helpers';

type IProps = IEditWeekWorkTimeOwnProps &
	ReturnType<typeof stateToEditWeeklyWorkTimeProps> &
	ReturnType<typeof dispatchToProps> &
	ITranslateProps &
	InjectedFormProps<IBookingTimeTableForm>;

interface IState {
	weekIndexSelected: number;
	weekCount: number;
	maxEndDate: Date;
	minEndDate: Date;
	hasUserSetMaxDate: boolean;
}

@translate(c.DEFAULT_TRANSLATION_NAMESPACE)
class EditWeekWorkTimeComponent extends React.Component<IProps, IState> {
	public constructor(props: IProps) {
		super(props);

		this.state = {
			weekIndexSelected: 0,
			weekCount: 1,
			maxEndDate: null,
			minEndDate: null,
			hasUserSetMaxDate: false,
		};

		this.submit = this.submit.bind(this);
		this.renderDay = this.renderDay.bind(this);
		this.setHasUserSetMaxDate = this.setHasUserSetMaxDate.bind(this);
	}

	public componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>) {
		// If week count increased / decreased change endTime if user didn't manually select an end time already
		if (!this.state.hasUserSetMaxDate && prevState.weekCount !== this.state.weekCount) {
			const lastDay = moment(this.props.currentFormValues.startDate).clone().endOf('week');
			const newEndDate = lastDay.add(this.state.weekCount - 1, 'weeks');
			const roundedLastDay = newEndDate.clone().startOf('hour').add(1, 'hour'); // Round to the start of the hour
			this.props.change('endDate', roundedLastDay.toISOString());
		}

		// Set minEndDate when changing weekCount
		if (prevState.weekCount !== this.state.weekCount) {
			this.setState({
				minEndDate: moment(this.props.currentFormValues.workTime[this.state.weekCount - 1][6].date).toDate(),
			});
		}

		// Initial set minEndDate
		if (this.state.minEndDate === null && this.props.currentFormValues?.workTime) {
			this.setState({
				minEndDate: moment(this.props.currentFormValues.workTime[this.state.weekCount - 1][6].date).toDate(),
			});
		}

		// Initial set maxEndDate
		if (this.state.maxEndDate === null && this.props.currentFormValues.startDate) {
			this.setState({
				maxEndDate: moment(this.props.currentFormValues.startDate).add(1, 'year').endOf('week').toDate(),
			});
		}

		// Set max date for end time (1 year max)
		if (
			prevProps?.currentFormValues?.startDate &&
			prevProps.currentFormValues.startDate.toString() !== this.props.currentFormValues.startDate.toString()
		) {
			this.setState({
				maxEndDate: moment(this.props.currentFormValues.startDate).clone().add(1, 'year').endOf('week').toDate(),
			});
		}
	}

	private get canSubmit(): boolean {
		if (this.props.isFetching) {
			return false;
		}

		return true;
	}

	private submit(form: IBookingTimeTableForm) {
		if (!this.canSubmit) {
			return;
		}

		this.props.submitForm(
			() => this.props.state,
			this.props.t,
			form,
			this.state.weekCount,
			this.props.currentFormValues.startDate,
			this.props.currentFormValues.endDate,
		);
	}

	private setHasUserSetMaxDate() {
		this.setState({ hasUserSetMaxDate: true });
	}

	private renderDay(d: number) {
		const { t, office, monday, currentFormValues } = this.props;
		const limits = dayMinMaxTime(office, monday, d);
		const initialLimits = officeMinMaxTime(office, monday, d);
		const msDay = d === 6 ? 0 : d + 1; // ISO to Microsoft
		const isSalonClosed: boolean = !limits[0] || !limits[1] ? true : false;
		const timezone = office?.zoneInfo;
		let workSeconds = 0;

		if (currentFormValues?.workTime) {
			const dayPeriods = this.props.currentFormValues?.workTime[this.state.weekIndexSelected][d].periods;
			const dayActive: boolean = this.props.currentFormValues?.workTime[this.state.weekIndexSelected][d].active;

			// Calculate work hours
			if (dayActive) {
				dayPeriods.map((period) => {
					if (period.startTime && period.endTime) {
						const startUnix =
							momentWithTimezone(timezone, period.startTime).unix() -
							momentWithTimezone(timezone, period.startTime).startOf('day').unix();
						const endUnix =
							momentWithTimezone(timezone, period.endTime).unix() -
							momentWithTimezone(timezone, period.endTime).startOf('day').unix();

						workSeconds += endUnix - startUnix;
					}
				});
			}
		}

		return (
			<PortalPageSection key={d} className={`${isSalonClosed ? 'disabled' : ''}`}>
				<WorkDayPeriods
					rootKey={String(d)}
					dayLabel={
						<>
							<span className="weeklyWorkTime__day">{t('Day_' + msDay)}</span>
							<span className="weeklyWorkTime__sideText">
								{isSalonClosed
									? t('Shop_WorkTime_Error_Closed')
									: workSeconds !== 0
									  ? formatDuration(workSeconds)
									  : t('Shop_WorkTime_NoWorkTime')}
							</span>
						</>
					}
					minTime={limits[0]}
					maxTime={limits[1]}
					initialMinTime={initialLimits[0]}
					initialMaxTime={initialLimits[1]}
					weekIndexSelected={this.state.weekIndexSelected}
					array={this.props.array}
					submitting={this.props.submitting}
					change={this.props.change}
					timezone={timezone}
				/>
			</PortalPageSection>
		);
	}

	public render() {
		const { t, handleSubmit, resource } = this.props;
		const title = (
			<h1 className="weeklyWorkTime__title">
				<strong>{t('Shop_WorkTime_WeeklySchedule')}</strong>
			</h1>
		);
		const subtitle = <p className="weeklyWorkTime__subtitle">{t('Shop_WorkTime_SetWeekly')}</p>;
		const weekSelectArray = [];

		for (let i = 0; i < this.state.weekCount; i = i + 1) {
			weekSelectArray.push(
				<span className={`weeklyWorkTime__week ${this.state.weekIndexSelected === i ? 'active' : ''}`} key={`week${i}`}>
					<span
						onClick={() => {
							this.setState({ weekIndexSelected: i });
						}}
					>
						{t('Periodselector_Periodicity_1')} {i + 1}
					</span>

					{this.state.weekCount > 1 &&
						renderIcon('cancel', 18, 18, 'weeklyWorkTime__removeWeek', () => {
							const workTimeArray: IBookingTimeTableForm[] = JSON.parse(
								JSON.stringify(this.props.currentFormValues.workTime),
							);

							for (let j = i; j < workTimeArray.length - 1; j++) {
								workTimeArray[j].forEach((day, idx) => {
									// Move the day periods and is active flags of the next week's days to the current one
									day.periods = workTimeArray[j + 1][idx].periods;
									day.active = workTimeArray[j + 1][idx].active;
									// Delete the next week's day periods and is active flags
									workTimeArray[j + 1][idx].periods = [];
									workTimeArray[j + 1][idx].active = false;
								});
							}

							// If removing last week reset periods and active flag
							if (i === 3 && this.state.weekCount === 4) {
								workTimeArray[i].forEach((day) => {
									day.active = false;
									day.periods = [];
								});
							}

							if (this.state.weekCount === this.state.weekIndexSelected + 1) {
								this.setState((state) => ({
									weekIndexSelected: state.weekIndexSelected - 1,
									weekCount: state.weekCount - 1,
								}));
							} else {
								this.setState({ weekCount: this.state.weekCount - 1 });
							}
							// Be careful not to manipulate the date inside each workTime week array
							this.props.change('workTime', workTimeArray);
						})}
				</span>,
			);
		}

		if (this.state.weekCount < 4) {
			weekSelectArray.push(
				renderIcon(
					'nikaPlus',
					20,
					20,
					'weeklyWorkTime__addWeek',
					() => {
						this.setState({ weekCount: this.state.weekCount + 1 });
					},
					false,
					`addWeek`,
				),
			);
		}

		return (
			<Modal size="medium" modalClass="modal-with-form weeklyWorkTime">
				<div className="weeklyWorkTime__headerContainer">
					<div>
						{title}
						{subtitle}
					</div>

					<div>
						<div className="weeklyWorkTime__closeButton" onClick={this.props.closeModal}>
							{renderIcon('cancel', 24, 24)}
						</div>
					</div>
				</div>

				<div className="weeklyWorkTime__resourceContainer">
					<img
						className="weeklyWorkTime__picture"
						src={resource?.picture ? resource.picture : require('containers/Html/icons/pfp_default.svg')}
					/>

					<div className="weeklyWorkTime__nameTitle">
						<span>{resource.name}</span>
						<span>{resource.jobTitle}</span>
					</div>
				</div>

				<form onSubmit={handleSubmit(this.submit)}>
					<PortalPageSection variant="contents">
						<div className="row d-sm-flex">
							<div className="col-md-6 col-12">
								<Field
									name={'startDate'}
									type="text"
									component={BookingDatePicker}
									label={t('Shop_WorkTime_StartDate')}
									information={t('Shop_WorkTime_StartDate')}
									validate={[required]}
									minDate={momentWithTimezone().startOf('week').toDate()}
									hideYearArrows={true}
								/>
							</div>

							<div className="col-md-6 col-12">
								<Field
									name={'endDate'}
									type="text"
									component={BookingDatePicker}
									label={t('Shop_WorkTime_EndDate')}
									information={t('Shop_WorkTime_EndDate')}
									validate={[required]}
									maxDate={this.state.maxEndDate}
									minDate={this.state.minEndDate}
									customValueChangeFunction={() => this.setHasUserSetMaxDate()}
									hideYearArrows={true}
								/>
							</div>

							<div className="col-12">
								<div className="weeklyWorkTime__weekSelection no-scroll-bar">{weekSelectArray}</div>
							</div>
						</div>

						{[0, 1, 2, 3, 4, 5, 6].map(this.renderDay)}
					</PortalPageSection>

					<ButtonsSection align="right">
						<WhiteButton onClick={this.props.closeModal}>{t('BSPortal_Common_Cancel')}</WhiteButton>
						<Button type="submit" disabled={!this.canSubmit}>
							{t('BSPortal_Common_Save')}
						</Button>
					</ButtonsSection>
				</form>
			</Modal>
		);
	}
}

let EditWeekWorkTime = reduxForm(
	{
		form: c.FORM_NAME_WORK_TIME,
		enableReinitialize: true,
	},
	stateToEditWeeklyWorkTimeProps,
)(EditWeekWorkTimeComponent);

const dispatchToProps = (dispatch: Dispatch<IStore>, ownProps: IEditWeekWorkTimeOwnProps) => ({
	submitForm: (
		getState: () => IStore,
		t,
		form: IBookingTimeTableForm,
		weekCount: number,
		startDate: Date,
		endDate: Date,
	) => submitForm(getState, dispatch, t, 'week', ownProps, form, weekCount, startDate, endDate),
	closeModal: () => dispatch(closeModal()),
});

EditWeekWorkTime = connect(stateToEditWeeklyWorkTimeProps, dispatchToProps)(EditWeekWorkTime);

export { EditWeekWorkTime };
